import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Money as CashIcon,
  ArrowBack as BackIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import ReportDataGrid from '../components/ReportDataGrid';
import reportService from '../services/reportService';
import { useNavigate } from 'react-router-dom';

const PaymentCollectionReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [filters, setFilters] = useState({
    paymentMethodId: '',
    customerId: '',
    startDate: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    endDate: new Date(),
  });
  const [flattenedData, setFlattenedData] = useState([]);

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {
        paymentMethodId: filters.paymentMethodId,
        startDate: filters.startDate?.toISOString().split('T')[0],
        endDate: filters.endDate?.toISOString().split('T')[0],
      };

      if (filters.customerId) {
        params.customerId = filters.customerId;
      }

      const response = await reportService.getPaymentCollection(params);
      
      if (response.success) {
        setReportData(response.data || []);
        setTotals(response.totals);
        setPaymentMethods(response.paymentMethods || []);
        
        // Flatten all payments for DataGrid
        const flatPayments = [];
        response.data.forEach(group => {
          group.invoices.forEach(payment => {
            flatPayments.push({
              ...payment,
              paymentMethodName: group.paymentMethodName,
              paymentMethodCategory: group.category,
              paymentMethodId: group.paymentMethodId
            });
          });
        });
        setFlattenedData(flatPayments);
      }
    } catch (error) {
      console.error('Error fetching payment collection report:', error);
      alert('Failed to fetch payment collection report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchReport();
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    fetchReport();
  };

  // Handle back
  const handleBack = () => {
    navigate('/reports');
  };

  // Define columns for DataGrid
  const columns = [
    {
      field: 'paymentMethodName',
      headerName: 'Payment Method',
      flex: 1,
      minWidth: 150,
      sortable: true,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const category = params.row.paymentMethodCategory;
        const categoryColors = {
          cash: { bg: '#e8f5e9', text: '#2e7d32' },
          card: { bg: '#e3f2fd', text: '#1565c0' },
          bank_transfer: { bg: '#f3e5f5', text: '#6a1b9a' },
          mobile_wallet: { bg: '#fff3e0', text: '#e65100' },
          other: { bg: '#f5f5f5', text: '#616161' },
        };
        const color = categoryColors[category] || categoryColors.other;
        
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '24px',
            }}
          />
        );
      },
    },
    {
      field: 'invoiceNo',
      headerName: 'Invoice No',
      flex: 0.8,
      minWidth: 120,
      sortable: true,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 0.9,
      minWidth: 120,
      sortable: true,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      },
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1.2,
      minWidth: 180,
      sortable: true,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params) => params.row.customerName || '-',
    },
    {
      field: 'amount',
      headerName: 'Payment Amount',
      flex: 1,
      minWidth: 140,
      type: 'number',
      resizable: true,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => `SAR ${params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      cellClassName: () => 'font-weight-bold',
    },
    {
      field: 'reference',
      headerName: 'Reference',
      flex: 0.9,
      minWidth: 120,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params) => params.row.reference || '-',
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 150,
      resizable: true,
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params) => params.row.notes || '-',
    },
    {
      field: 'paymentStatus',
      headerName: 'Invoice Status',
      flex: 0.8,
      minWidth: 120,
      resizable: true,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const status = params.value;
        const colors = {
          paid: { bg: '#e8f5e9', text: '#2e7d32' },
          'partially paid': { bg: '#fff3e0', text: '#f57c00' },
          pending: { bg: '#ffebee', text: '#c62828' },
        };
        const color = colors[status] || colors.pending;
        
        return (
          <Chip
            label={status?.toUpperCase() || 'PENDING'}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: '22px',
            }}
          />
        );
      },
    },
  ];

  // Get category icon and color
  const getCategoryStyle = (category) => {
    const styles = {
      cash: { icon: <CashIcon />, color: '#2e7d32', bg: '#e8f5e9' },
      card: { icon: <CardIcon />, color: '#1565c0', bg: '#e3f2fd' },
      bank_transfer: { icon: <BankIcon />, color: '#6a1b9a', bg: '#f3e5f5' },
      mobile_wallet: { icon: <CashIcon />, color: '#e65100', bg: '#fff3e0' },
      other: { icon: <ReceiptIcon />, color: '#616161', bg: '#f5f5f5' },
    };
    return styles[category] || styles.other;
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {/* Header */}
      <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
        <IconButton 
          onClick={handleBack}
          sx={{ mr: 1.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
        >
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            Payment Collection Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tracks received payments by mode (cash, card, bank transfer, etc.)
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <Typography variant="caption" fontWeight="600" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
          Filters
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={filters.paymentMethodId}
                onChange={(e) => handleFilterChange('paymentMethodId', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: '36px',
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 14px',
                  },
                }}
              >
                <MenuItem value="">All Payment Methods</MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method._id} value={method._id}>
                    {method.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        height: '36px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 14px',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        height: '36px',
                        fontSize: '0.875rem',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.875rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 14px',
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  borderRadius: 0,
                  height: '36px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Apply Filters'}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Payment Mode Totals */}
      {reportData.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {reportData.map((paymentGroup, index) => {
            const categoryStyle = getCategoryStyle(paymentGroup.category);
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    borderLeft: `4px solid ${categoryStyle.color}`,
                    bgcolor: categoryStyle.bg,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        bgcolor: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      }}
                    >
                      {React.cloneElement(categoryStyle.icon, { sx: { color: categoryStyle.color, fontSize: 20 } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="700" sx={{ color: categoryStyle.color, fontSize: '0.875rem', mb: 0.25 }}>
                        {paymentGroup.paymentMethodName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {paymentGroup.transactionCount} {paymentGroup.transactionCount === 1 ? 'transaction' : 'transactions'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ bgcolor: 'white', p: 1, borderRadius: '4px' }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                      Total Collected
                    </Typography>
                    <Typography variant="h6" fontWeight="700" sx={{ color: categoryStyle.color, fontSize: '1.1rem' }}>
                      SAR {paymentGroup.paidAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Payment Collections DataGrid */}
      <ReportDataGrid
        data={flattenedData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`payment-collection-${new Date().toISOString().split('T')[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) => row.paymentId || `${row.invoiceNo}-${row.paymentDate}`}
      />
    </Container>
  );
};

export default PaymentCollectionReport;
